import {McpServerDialog} from '@github-ui/configure-mcp-server-partial/McpServerDialog'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {DashIcon, PlusIcon, SearchIcon, ToolsIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Spinner, TextInput, TreeView} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import React, {useMemo, useState} from 'react'

import type {ToolWithDescription} from '../utils/copilot-chat-service'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import styles from './McpToolsManager.module.css'

interface McpToolsManagerProps {
  anchorRef: React.RefObject<HTMLButtonElement>
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const McpToolsManager: React.FC<McpToolsManagerProps> = ({anchorRef, open, onOpenChange}) => {
  const [showAddMcpDialog, setShowAddMcpDialog] = useState(false)
  const [filter, setFilter] = useState('')

  // only a single item can be the "current" one, per the docs: https://primer.style/product/components/tree-view/#TreeView.Item
  const [currentItem, setCurrentItem] = useState<string | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set())

  const {service} = useChatManager()
  const queryClient = useQueryClient()
  const {data, isPending, isError} = useQuery({
    enabled: open,
    queryKey: ['copilot-chat-mcps'],
    queryFn: async () => service.fetchMCPServers(),
    placeholderData: prev => prev,
  })

  const servers = useMemo(() => data || [], [data])

  const filteredServers = useMemo(
    () => servers.filter(server => server.name.toLowerCase().includes(filter.toLowerCase())),
    [servers, filter],
  )

  const handleServerSelect = (serverId: string) => {
    setCurrentItem(serverId)
    toggleExpand(serverId)
  }

  const toggleExpand = (serverId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(serverId)) {
        newSet.delete(serverId)
      } else {
        newSet.add(serverId)
      }
      return newSet
    })
  }

  const handleAddMcpClick = () => {
    setShowAddMcpDialog(true)
  }

  const handleDialogClose = () => {
    setShowAddMcpDialog(false)
  }

  const handleServerAdded = () => {
    // Refresh the server list when a new server is added
    void queryClient.invalidateQueries({queryKey: ['copilot-chat-mcps']})
  }

  const isToolWithDescriptionArray = (tools: Array<string | ToolWithDescription>): tools is ToolWithDescription[] => {
    return tools.length > 0 && typeof tools[0] === 'object' && 'name' in tools[0]
  }

  return (
    <>
      <AnchoredOverlay
        key="mcp-tool-manager"
        open={open}
        onOpen={() => onOpenChange(true)}
        onClose={() => onOpenChange(false)}
        anchorRef={anchorRef}
        side="outside-bottom"
        align="end"
        renderAnchor={() => <></>}
        width="medium"
      >
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerTitle}>MCP tools</div>
            <TextInput
              placeholder="Search by provider"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className={styles.searchInput}
              leadingVisual={SearchIcon}
            />
          </div>

          {/* Content */}
          <div className={styles.contentArea}>
            {isPending ? (
              <div className={styles.loadingContainer}>
                <Spinner />
              </div>
            ) : isError ? (
              <Blankslate>
                <Blankslate.Visual>⚠️</Blankslate.Visual>
                <Blankslate.Heading>Error loading tools</Blankslate.Heading>
              </Blankslate>
            ) : servers.length === 0 ? (
              <div className={styles.emptyState}>
                <ToolsIcon size={24} />
                <div className={styles.emptyStateTitle}>You don&apos;t have any MCP tools configured</div>
                <div className={styles.emptyStateDescription}>
                  Add one to connect models to files, APIs, databases, and more.
                </div>
              </div>
            ) : (
              <TreeView aria-label="MCP server selection">
                {filteredServers.map(server => (
                  <TreeView.Item
                    key={server.id}
                    id={server.id}
                    onSelect={() => handleServerSelect(server.id)}
                    expanded={expandedItems.has(server.id)}
                    onExpandedChange={() => toggleExpand(server.id)}
                    current={currentItem === server.id}
                    aria-selected={currentItem === server.id}
                  >
                    <span className={styles.serverName}>{server.name}</span>
                    {server.tools && server.tools.length > 0 ? (
                      <TreeView.SubTree>
                        {isToolWithDescriptionArray(server.tools)
                          ? server.tools.map(tool => {
                              const toolId = `${server.id}-${tool.name}`
                              const isToolSelected = currentItem === toolId
                              return (
                                <TreeView.Item
                                  key={tool.name}
                                  id={toolId}
                                  current={isToolSelected}
                                  aria-selected={isToolSelected}
                                  onSelect={() => {
                                    if (!isToolSelected && copilotFeatureFlags.mcpToolDescriptions) {
                                      setCurrentItem(toolId)
                                    } else {
                                      setCurrentItem(null)
                                    }
                                  }}
                                >
                                  {copilotFeatureFlags.mcpToolDescriptions ? (
                                    <div className={styles.toolItem}>
                                      <div className={styles.toolNameContainer}>
                                        {isToolSelected ? <DashIcon size={12} /> : <PlusIcon size={12} />}
                                        <span className={styles.toolName}>{tool.name}</span>
                                      </div>
                                      {tool.description && isToolSelected && (
                                        <div className={styles.toolDescription}>{tool.description}</div>
                                      )}
                                    </div>
                                  ) : (
                                    tool.name
                                  )}
                                </TreeView.Item>
                              )
                            })
                          : server.tools.map(tool => {
                              const toolId = `${server.id}-${tool}`

                              return (
                                <TreeView.Item
                                  key={tool}
                                  id={toolId}
                                  onSelect={() => setCurrentItem(toolId)}
                                  current={currentItem === toolId}
                                  aria-selected={currentItem === toolId}
                                >
                                  {tool}
                                </TreeView.Item>
                              )
                            })}
                      </TreeView.SubTree>
                    ) : null}
                  </TreeView.Item>
                ))}
              </TreeView>
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <Button variant="default" leadingVisual={PlusIcon} onClick={handleAddMcpClick} block>
              New tool
            </Button>
          </div>
        </div>
      </AnchoredOverlay>

      <McpServerDialog isOpen={showAddMcpDialog} onClose={handleDialogClose} onServerAdded={handleServerAdded} />
    </>
  )
}

try{ McpToolsManager.displayName ||= 'McpToolsManager' } catch {}